import type { ServiceResponse } from "@/types/services";

type AccessTokenGetter = () => string | null;

let getAccessTokenImpl: AccessTokenGetter = () => null;
let unauthorizedHandler: (() => void) | null = null;
let hasHandledUnauthorized = false;

/**
 * Wires the API client to the auth provider. Called once by `AuthProvider` so
 * that services can read the current token without it being threaded through
 * every service function call.
 */
export function registerApiClient(config: {
  getAccessToken: AccessTokenGetter;
  onUnauthorized?: () => void;
}) {
  getAccessTokenImpl = config.getAccessToken;
  unauthorizedHandler = config.onUnauthorized ?? null;
}

function handleUnauthorized() {
  if (hasHandledUnauthorized) return;
  hasHandledUnauthorized = true;
  unauthorizedHandler?.();
  // Allow future 401s to trigger re-auth again once this one has been handled.
  setTimeout(() => {
    hasHandledUnauthorized = false;
  }, 5000);
}

/**
 * Authenticated fetch wrapper used by every service. Reads the access token
 * itself, always resolves with a `ServiceResponse` (never throws to the UI),
 * and centralizes request/response logging and 401 handling.
 */
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
): Promise<ServiceResponse<T>> {
  if (import.meta.env.DEV) {
    const { isOfflineMode } = await import("@/dev/offline-mode");
    if (isOfflineMode()) {
      const { resolveOfflineRequest } = await import("@/dev/offline-fixtures");
      return resolveOfflineRequest<T>(url, options);
    }
  }

  const baseURL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
  const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;
  const accessToken = getAccessTokenImpl();

  const headers = new Headers(options.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const requestTime = Date.now();
  if (import.meta.env.DEV) {
    console.log("📤 API Request:", {
      method: options.method || "GET",
      url: fullUrl,
      body:
        typeof options.body === "string"
          ? JSON.parse(options.body)
          : undefined,
      timestamp: new Date().toISOString(),
    });
  }

  try {
    const response = await fetch(fullUrl, { ...options, headers });

    if (response.status === 401) {
      handleUnauthorized();
    }

    const json = (await response
      .json()
      .catch(() => null)) as ServiceResponse<T> | null;

    if (import.meta.env.DEV) {
      console.log("📥 API Response:", {
        method: options.method || "GET",
        url: fullUrl,
        status: response.status,
        data: json,
        duration: `${Date.now() - requestTime}ms`,
        timestamp: new Date().toISOString(),
      });
    }

    if (json) return json;

    return {
      ok: false,
      statusCode: response.status,
      message: response.statusText || "Request failed",
      data: null as T,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("❌ API Error:", {
        method: options.method || "GET",
        url: fullUrl,
        error: error instanceof Error ? error.message : String(error),
        duration: `${Date.now() - requestTime}ms`,
      });
    }

    return {
      ok: false,
      statusCode: 0,
      message: error instanceof Error ? error.message : "Network error",
      data: null as T,
    };
  }
}
